import type { SessionState, CareLoadState } from './types'
import { getLoadLabel } from './types'

/**
 * Deterministic, non-authoritative "mirror" reading.
 * Never diagnoses, never gives advice, never claims to know the user.
 * Only reflects what they logged, in precise, slightly strange instrument language.
 * This is the primary path. Workers AI (when present) produces one additional short mirror note only.
 */
export function synthesizeReading(state: SessionState): string {
  const load = state.load
  const total = load.total
  const label = getLoadLabel(total)
  const frag = Object.values(state.responses).find(r => r.fieldKey === 'frag_loops')?.value || []
  const fragCount = Array.isArray(frag) ? frag.length : (state.responses['frag_count']?.value || 0)
  const recovery = Object.values(state.responses).find(r => r.fieldKey === 'recovery_vectors')?.value || []
  const clarity = (Object.values(state.responses).find(r => r.fieldKey === 'clarity_note')?.value || '').trim()
  const somatic = Object.values(state.responses).find(r => r.fieldKey === 'readiness_somatic')?.value || ''
  const shape = (Object.values(state.responses).find(r => r.fieldKey === 'situating_shape')?.value || '').toString().slice(0, 120)

  const uCount = state.uncertaintyTotal || 0

  const lines: string[] = []

  lines.push(`Care load registers at ${total.toFixed(1)} — ${label}.`)

  if (load.diffuse) {
    lines.push('The load presents as diffuse; the strata do not resolve into distinct contributors today.')
  } else {
    const top = [...load.strata].sort((a, b) => b.value - a.value)[0]
    if (top && top.value >= 2.5) {
      lines.push(`Dominant contributor in this reading: ${top.label.toLowerCase()} (${top.value.toFixed(1)}).`)
    }
  }

  if (fragCount >= 3) {
    lines.push(`${fragCount} distinct internal tracks are currently running.`)
  } else if (fragCount === 1 || fragCount === 2) {
    lines.push(`Attention appears held in ${fragCount} main track${fragCount > 1 ? 's' : ''}.`)
  }

  if (Array.isArray(recovery) && recovery.length > 0) {
    const first = recovery[0]?.toString().slice(0, 80)
    if (first) lines.push(`One named recovery vector: "${first}".`)
  }

  if (somatic && somatic !== 'unsure' && somatic !== 'none') {
    lines.push(`Somatic signal located: ${somatic}.`)
  }

  if (clarity && clarity.length > 12) {
    lines.push(`Post-reset clarity note present (${clarity.length} characters).`)
  }

  if (uCount >= 2) {
    lines.push(`${uCount} fields carried explicit uncertainty flags.`)
  }

  if (shape) {
    lines.push(`Ambiguity shape logged: ${shape}.`)
  }

  // Gentle closing that returns authority
  lines.push('This is a snapshot of one moment inside the instrument. You remain the only authority on what it means or what (if anything) you do with it.')

  return lines.join(' ')
}

/**
 * Optional narrow AI supplement (locked prompt on the worker side).
 * The client always shows the deterministic reading first.
 */
export function buildAIMirrorPrompt(state: SessionState): string {
  const snap = JSON.stringify({
    load: state.load,
    responses: state.responses,
    notes: state.fieldNotes?.slice(0, 280),
  })
  return `You are a quiet, non-authoritative instrument mirror. 
You receive only the raw logged state from a person using the Care Load Scale.
Produce ONE short (under 140 characters), precise, slightly strange descriptive sentence that reflects the numbers and words they entered.
Rules:
- No diagnosis, no "you are feeling", no "you should", no advice, no interpretation of character.
- Use the exact load number and one or two of their own phrases if they fit naturally.
- End by returning authority: the person is the sole author of meaning.
Return only the sentence. No quotes, no labels.

State: ${snap}`
}
